/**
 * Elevated property card — the single card used by the lodging homepage
 * `featured_properties` section and the /properties list. Both surfaces
 * consume the same `/api/properties` summary shape, so keep this the only
 * place a property card is styled.
 *
 * Visual contract: rounded-2xl media card with soft shadow + hover lift,
 * rating chip (terra star) and from-price chip overlaid on the photo,
 * serif display title, metadata row pinned to the card foot.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bath, Bed, MapPin, Star, Users } from 'lucide-react';
import { formatPrice } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface PropertyCardData {
  id: string;
  slug: string;
  title: string;
  short_description?: string | null;
  city?: string | null;
  region?: string | null;
  max_guests: number;
  bedrooms: number | string;
  bathrooms: number | string;
  base_price_amount: number | string;
  teaser_min_price?: number | string | null;
  teaser_max_price?: number | string | null;
  pricing_source?: string | null;
  currency?: string;
  primary_image_url?: string | null;
  rating_avg?: number | string | null;
  reviews_count?: number;
}

interface PropertyCardProps {
  property: PropertyCardData;
  /** Query string (starting with `?`) appended to the detail link so chosen
   * dates/guests survive the click-through. */
  detailQs?: string;
  showPrice?: boolean;
  showDescription?: boolean;
  className?: string;
}

export default function PropertyCard({
  property: p,
  detailQs = '',
  showPrice = true,
  showDescription = false,
  className,
}: PropertyCardProps) {
  const { t } = useTranslation('properties');
  const rating = p.rating_avg != null ? Number(p.rating_avg) : null;
  const priceFmt = (n: number) => formatPrice(n, {
    currency: p.currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const fromPrice = priceFmt(Number(p.base_price_amount));
  const hasRange = p.teaser_min_price != null && p.teaser_max_price != null
    && Number(p.teaser_min_price) !== Number(p.teaser_max_price);
  const minPrice = hasRange ? priceFmt(Number(p.teaser_min_price)) : null;
  const maxPrice = hasRange ? priceFmt(Number(p.teaser_max_price)) : null;
  const isDynamic = p.pricing_source === 'ownerrez';

  return (
    <Link
      to={`/properties/${p.slug}${detailQs}`}
      className={cn(
        'group flex flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm',
        'transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
        className,
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-surface-muted">
        {p.primary_image_url ? (
          <img
            src={p.primary_image_url}
            alt={p.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-ink-muted">
            {t('list.card.noPhoto')}
          </div>
        )}
        {rating !== null && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-surface/90 px-2.5 py-1 text-xs font-medium text-ink shadow-sm backdrop-blur-sm">
            <Star className="h-3.5 w-3.5 fill-terra text-terra" aria-hidden="true" />
            {rating.toFixed(1)}
            {p.reviews_count ? <span className="text-ink-muted">({p.reviews_count})</span> : null}
          </span>
        )}
        {showPrice && (
          <span className="absolute bottom-3 left-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
            {hasRange
              ? t('list.card.priceRange', { min: minPrice, max: maxPrice })
              : `${t('list.card.fromPrice', { amount: fromPrice })} ${t('list.card.perNight')}`}
            {isDynamic && (
              <span className="ml-1 opacity-70" title={t('list.card.priceVaries')}>·</span>
            )}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4">
          <h3 className="font-display text-lg font-bold text-ink transition-colors line-clamp-1 group-hover:text-sage-700">
            {p.title}
          </h3>
          {(p.city || p.region) && (
            <span className="mt-1 flex items-center gap-1 text-sm text-ink-muted">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {[p.city, p.region].filter(Boolean).join(', ')}
            </span>
          )}
          {showDescription && p.short_description && (
            <p className="mt-2 text-sm text-ink-soft line-clamp-2">{p.short_description}</p>
          )}
        </div>
        <div className="mt-auto flex items-center gap-4 border-t border-line pt-3 text-xs text-ink-muted">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" aria-hidden="true" />
            {t('list.card.guestsCount', { count: p.max_guests })}
          </span>
          <span className="flex items-center gap-1">
            <Bed className="h-3.5 w-3.5" aria-hidden="true" />
            {t('list.card.bedroomsShort', { count: Number(p.bedrooms) })}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="h-3.5 w-3.5" aria-hidden="true" />
            {t('list.card.bathroomsShort', { count: Number(p.bathrooms) })}
          </span>
        </div>
      </div>
    </Link>
  );
}
